import React, { useState, useEffect } from "react";

import { useForm } from "react-hook-form";

import Button from "../../../../components/Button/Button";
import Textarea from "../../../../components/Textarea/Textarea";
import Selector from "../../../../components/Dropdown/DropdownField";
import ImageInput from "../../../../components/ImageInput/ImageInput";
import InputField from "../../../../components/InputField/InputField";

import "./UnitForm.scss";
import http from "../../../../utils/http";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { AxiosResponse } from "axios";

type UnitType = {
  description: string;
  id: number;
  name: string;
};
interface ICourse {
  id: string;
  name: string;
  description: string;
  thumbnail: null | string | HTMLInputElement;
}
interface ISubject {
  id: number;
  name: string;
  course: ICourse;
  thumbnail: string;
  description: string;
}
interface ICourseResponse extends AxiosResponse {
  data: { data: ICourse[]; message: string };
}
interface ISubjectResponse extends AxiosResponse {
  data: { data: ISubject[]; message: string };
}

type PropsType = {
  data?: UnitType | any;
  editform?: boolean | any;
  handleCancel?: Function | any;
  handleClickSubmit?: Function | any;
  handleClickUpdate?: Function | any;
};

interface IResponse extends AxiosResponse {
  data: { data: object[]; message: string };
}

const UnitForm = ({
  data,
  editform,
  handleCancel,
  handleClickSubmit,
  handleClickUpdate
}: PropsType) => {
  const [image, setImage] = useState(editform ? data.thumbnail : "");

  const {
    reset,
    watch,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm(
    editform && {
      defaultValues: {
        id: data && data.id,
        name: data && data.name,
        subject: data && data.subject.id,
        thumbnail: data && data.thumbnail,
        description: data && data.description,
        course: data && data.subject.course.id
      }
    }
  );

  const watchCourse = watch("course");
  const [courses, setCourses] = useState<ICourse[] | any>([]);
  const [subjects, setSubjects] = useState<ISubject[] | any>([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);

  useEffect(() => {
    async function getData() {
      try {
        let promiseList:
          | Promise<ISubjectResponse>[]
          | Promise<ICourseResponse>[] = [];
        promiseList.push(http.GET("school/course/list", ""));
        promiseList.push(http.GET("school/subject/list", ""));

        const allPromise = await Promise.all(promiseList);

        const courses = allPromise[0].data.data.map(each => {
          let formattedCourses = {};
          formattedCourses[each.id] = each.name;
          return formattedCourses;
        });
        setCourses(courses);

        const subjects = allPromise[1].data.data;
        setSubjects(subjects);
      } catch (err) {
        toast.error(err);
      }
    }
    getData();
  }, []);

  useEffect(() => {
    if (watchCourse !== "Select") {
      const filteredSubjects = subjects
        .filter(each => each.course.id === parseInt(watchCourse))
        .map(each => {
          let formattedSubject = {};
          formattedSubject[each.id] = each.name;
          return formattedSubject;
        });
      setFilteredSubjects(filteredSubjects);
    } else {
      const filteredSubjects = subjects.map(each => {
        let formattedSubject = {};
        formattedSubject[each.id] = each.name;
        return formattedSubject;
      });
      setFilteredSubjects(filteredSubjects);
    }
  }, [watchCourse, subjects, data]);

  const handleReset = () => {
    reset({
      name: "",
      thumbnail: "",
      description: "",
      course: "Select",
      subject: "Select"
    });
  };

  const handleClear = () => {
    handleReset();
    setImage("");
  };

  const [selectedCourse, setSelectedCourse] = useState(
    editform && data.subject.course.id
  );

  const [selectedSubject, setSelectedSubject] = useState(
    editform && data.subject.id
  );

  return (
    <form
      className="unit-form-container"
      onSubmit={handleSubmit(
        editform ? handleClickUpdate()(handleCancel) : handleClickSubmit
      )}
    >
      <div className="row-container">
        <div className="col-container">
          <div className="row-container">
            <div className="unit_image">
              <ImageInput
                image={image}
                editform={editform}
                setImage={setImage}
                {...register("thumbnail", {
                  required: false
                })}
              />
            </div>

            <div className="col-container">
              <div className="row-container">
                <div className="fieldAndValidate">
                  <Selector
                    required
                    label="Course"
                    options={courses}
                    editform={editform}
                    setSelectValue={setSelectedCourse}
                    selectValue={selectedCourse}
                    {...register("course", {
                      pattern: /^[1-9][0-9]*$/
                    })}
                  />
                  {errors?.course?.type === "pattern" && (
                    <p>This field is required</p>
                  )}
                </div>
                <div className="fieldAndValidate">
                  <Selector
                    required
                    label="Subject"
                    editform={editform}
                    options={filteredSubjects}
                    setSelectValue={setSelectedSubject}
                    selectValue={selectedSubject}
                    {...register("subject", {
                      pattern: /^[1-9][0-9]*$/
                    })}
                  />
                  {errors?.subject?.type === "pattern" && (
                    <p>This field is required</p>
                  )}
                </div>
              </div>
              <div className="fieldAndValidate">
                <InputField
                  required
                  type="text"
                  label="Unit Name"
                  placeholder={"Enter the unit name"}
                  {...register("name", {
                    required: true,
                    minLength: 2,
                    maxLength: 30
                  })}
                />
                {errors?.name?.type === "required" && (
                  <p>This field is required</p>
                )}
                {errors?.name?.type === "maxLength" && (
                  <p>Unit name too long, max 30 characters</p>
                )}
                {errors?.name?.type === "minLength" && (
                  <p>Unit name too short, min 2 characters</p>
                )}
              </div>
            </div>
          </div>
          <div className="fieldAndValidate">
            <Textarea
              label="Description"
              rows={4}
              placeholder="Enter course description"
              required={true}
              {...register("description", {
                minLength: 10,
                maxLength: 200
              })}
            />
            {errors?.description?.type === "maxLength" && (
              <p>Description too long, max 200 characters</p>
            )}
            {errors?.description?.type === "minLength" && (
              <p>Course name too short, min 10 characters</p>
            )}
          </div>
        </div>
      </div>

      <div className="row-container">
        <div className="button-wrapper">
          <Button
            clickHandler={() => {}}
            type="submit"
            color="success"
            buttonName={editform ? "Update Unit" : "Add Unit"}
          />
          {!editform && (
            <Button
              type="button"
              color="danger"
              buttonName="Clear"
              clickHandler={() => handleClear()}
            />
          )}
          {editform && (
            <Button
              type="button"
              color="danger"
              buttonName="Cancel"
              clickHandler={() => handleCancel()}
            />
          )}
        </div>
      </div>
    </form>
  );
};

export default UnitForm;
